/**
 * /admin — refonte « bonnes affaires » du 2026-09-14.
 *
 * Mêmes données et mêmes actions qu'avant (statistiques, analytics,
 * utilisateurs, affiliation, outils), sous la coque commune.
 *
 * Ajouté : la santé de la vitrine en tête des statistiques — combien de bonnes
 * affaires le site montre en ce moment. C'est le chiffre qui dit si le produit
 * tient sa promesse.
 *
 * Retirés : le « Score IA moyen » (score retiré des affichages), les prix écrits
 * à la main dans la fenêtre d'abonnement (« Particulier — 15€/mois » était faux :
 * c'est un paiement unique ; les prix viennent de config/offres.js), et la ligne
 * « Plan PROPASS » des informations : un administrateur n'a pas d'abonnement.
 *
 * 2026-10-22 : plus d'abonnement vendu. Retirés de l'admin : les tuiles
 * « Abonnements actifs » et « Revenus du mois », la colonne « Offre », les
 * boutons « Abonnement » / « Annuler l'abonnement » et la fenêtre qui
 * accordait une offre. Les routes /api/admin/users/{id}/subscription restent
 * côté serveur ; les libellés de plans ne servent plus qu'à l'historique
 * (résiliations, commissions d'affiliation).
 *
 * Mobile : les tableaux passent en fiches empilées sous 900 px. Un tableau large
 * dans un conteneur à défilement élargit quand même la fenêtre mobile (mesuré
 * sur /pricing le 2026-09-14).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Page from '../components/Layout/Page';
import useAffaires from '../components/Affaires/useAffaires';
import { nombre } from '../components/Affaires/format';
import AdminVente from './AdminVente';
import AdminGarages from './AdminGarages';
import AdminRecherche from './AdminRecherche';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './ComptePages.css';
import './AdminPage.css';

const ONGLETS = [
  ['stats', 'Statistiques'],
  ['analytics', 'Analytics'],
  ['users', 'Utilisateurs'],
  ['affiliate', 'Affiliation'],
  ['vente', 'Véhicules à vendre'],
  ['recherche', "Demandes d'achat"],
  ['garages', 'Garages'],
  ['tools', 'Outils'],
];

const LIBELLES_PLANS = {
  PARTICULIERPASS: 'Particulier',
  REVENDEURPASS: 'Achat-Revente',
  PROPASS: 'Pro Pass (legacy)',
  GARAGEPASS: 'Garage Pass',
  ULTRAPASS: 'UltraPass (legacy)',
  JEUNEPASS: 'JeunePass (legacy)',
};

const euros = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const jour = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

function Tuile({ label, valeur, sous, ton }) {
  return (
    <div className="zk-cpt-tuile">
      <p>{label}</p>
      <b className={ton || undefined}>{valeur ?? '—'}</b>
      {sous && <small className="zk-adm-sous">{sous}</small>}
    </div>
  );
}

function Puce({ plan }) {
  return <span className={`zk-adm-puce${plan ? '' : ' gratuit'}`}>{plan ? (LIBELLES_PLANS[plan] || plan) : 'Gratuit'}</span>;
}

function Graphique({ points }) {
  if (!points?.length) return <p className="zk-adm-vide">Pas encore de données</p>;
  const max = Math.max(1, ...points.map((d) => Math.max(d.visitors, d.signups)));
  const date = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  return (
    <div className="zk-carte">
      <div className="zk-adm-legende">
        <span><i className="v" />Visiteurs uniques</span>
        <span><i className="i" />Nouvelles inscriptions</span>
      </div>
      <div className="zk-adm-barres">
        {points.map((d) => (
          <div key={d.date} className="zk-adm-jour" title={`${d.date} — ${d.visitors} visiteur(s), ${d.signups} inscription(s)`}>
            <span className="v" style={{ height: `${Math.max(2, (d.visitors / max) * 100)}%` }} />
            <span className="i" style={{ height: `${Math.max(2, (d.signups / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="zk-adm-axe">
        <span>{date(points[0].date)}</span>
        <span>{date(points[Math.floor(points.length / 2)].date)}</span>
        <span>{date(points[points.length - 1].date)}</span>
      </div>
    </div>
  );
}

function Personne({ nom, email }) {
  return (
    <span className="zk-adm-personne">
      <b>{nom}</b>
      <small>{email}</small>
    </span>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { donnees: vitrine } = useAffaires({ par_page: 1 });

  const [onglet, setOnglet] = useState('stats');
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [affiliation, setAffiliation] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [message, setMessage] = useState(null);
  const [chargement, setChargement] = useState(false);
  // Dossiers au statut « nouveau » : ce qui attend une première action.
  const [nouveautes, setNouveautes] = useState({});

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (!user.is_admin) navigate('/dashboard');
  }, [user, navigate]);

  const signaler = useCallback((texte, ok = true) => {
    setMessage({ texte, ok });
    setTimeout(() => setMessage(null), 4000);
  }, []);

  const chargerStats = useCallback(async () => {
    try { setStats((await api.get('/api/admin/stats')).data); } catch { signaler('Erreur de chargement des statistiques', false); }
  }, [signaler]);

  const chargerAnalytics = useCallback(async () => {
    try { setAnalytics((await api.get('/api/admin/analytics/overview', { params: { days: 30 } })).data); } catch { signaler('Erreur de chargement des analytics', false); }
  }, [signaler]);

  const chargerUtilisateurs = useCallback(async () => {
    setChargement(true);
    try {
      setUtilisateurs((await api.get('/api/admin/users', { params: { search: recherche || undefined, limit: 100 } })).data);
    } catch { signaler('Erreur de chargement des utilisateurs', false); }
    setChargement(false);
  }, [recherche, signaler]);

  const chargerAffiliation = useCallback(async () => {
    setChargement(true);
    try { setAffiliation((await api.get('/api/admin/affiliate/stats')).data); } catch { signaler("Erreur de chargement de l'affiliation", false); }
    setChargement(false);
  }, [signaler]);

  // Une seule ligne demandée : seuls les compteurs servent ici, pas la liste.
  const chargerNouveautes = useCallback(async () => {
    const compter = async (url, statut) => {
      try { return (await api.get(url, { params: { limite: 1 } })).data?.comptes?.[statut] || 0; } catch { return 0; }
    };
    const [vente, recherche] = await Promise.all([
      compter('/api/admin/vente', 'nouveau'),
      compter('/api/admin/recherche', 'nouvelle'),
    ]);
    setNouveautes({ vente, recherche });
  }, []);

  useEffect(() => { if (user?.is_admin) chargerNouveautes(); }, [user, chargerNouveautes]);
  useEffect(() => { if (onglet === 'stats') chargerStats(); }, [onglet, chargerStats]);
  useEffect(() => { if (onglet === 'analytics') chargerAnalytics(); }, [onglet, chargerAnalytics]);
  // La recherche part sur « Chercher » ou Entrée, pas à chaque touche.
  useEffect(() => { if (onglet === 'users') chargerUtilisateurs(); }, [onglet]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (onglet === 'affiliate') chargerAffiliation(); }, [onglet, chargerAffiliation]);

  const majUtilisateur = (id, champs) => setUtilisateurs((us) => us.map((u) => (u.id === id ? { ...u, ...champs } : u)));

  async function basculerActif(id) {
    try {
      const r = await api.patch(`/api/admin/users/${id}/toggle-active`);
      majUtilisateur(id, { is_active: r.data.is_active });
      signaler(r.data.is_active ? 'Compte activé' : 'Compte désactivé');
    } catch { signaler('Erreur', false); }
  }

  async function basculerAdmin(id) {
    try {
      const r = await api.patch(`/api/admin/users/${id}/toggle-admin`);
      majUtilisateur(id, { is_admin: r.data.is_admin });
      signaler(r.data.is_admin ? 'Droits administrateur accordés' : 'Droits administrateur retirés');
    } catch { signaler('Erreur', false); }
  }

  async function supprimer(id, email) {
    if (!window.confirm(`Supprimer l'utilisateur ${email} ? Action irréversible.`)) return;
    try {
      await api.delete(`/api/admin/users/${id}`);
      setUtilisateurs((us) => us.filter((u) => u.id !== id));
      signaler('Utilisateur supprimé');
    } catch (e) { signaler(e.response?.data?.detail || 'Erreur', false); }
  }

  async function lancerScraping() {
    try {
      await api.post('/api/admin/trigger-scraping');
      signaler('Scraping lancé en arrière-plan');
    } catch { signaler('Erreur', false); }
  }

  async function purger() {
    if (!window.confirm('Désactiver les annonces de plus de 30 jours ?')) return;
    try {
      const r = await api.delete('/api/admin/listings/old', { params: { days: 30 } });
      signaler(r.data.message);
      chargerStats();
    } catch { signaler('Erreur', false); }
  }

  async function payer(id) {
    try {
      await api.patch(`/api/admin/affiliate/commission/${id}/pay`);
      signaler('Commission marquée comme payée');
      chargerAffiliation();
    } catch { signaler('Erreur', false); }
  }


  return (
    <Page largeur="large" titre="Administration" chapeau={<>Connecté en tant que <b>{user?.email}</b></>}>
      {message && (
        <div className={`zk-alerte ${message.ok ? 'ok' : 'erreur'}`} role="status" style={{ marginBottom: 14 }}>{message.texte}</div>
      )}

      <div className="zk-adm-onglets" role="tablist" aria-label="Sections">
        {ONGLETS.map(([id, libelle]) => (
          <button
            key={id} type="button" role="tab" aria-selected={onglet === id}
            className={`zk-aff-pilule${onglet === id ? ' on' : ''}`} onClick={() => setOnglet(id)}
          >
            {libelle}
            {nouveautes[id] > 0 && (
              <span className="zk-adm-badge" aria-label={`${nouveautes[id]} nouveau${nouveautes[id] > 1 ? 'x' : ''}`}>
                {nouveautes[id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {onglet === 'stats' && (
        <div>
          <h2 className="zk-adm-titre">La vitrine en ce moment</h2>
          <div className="zk-adm-tuiles">
            <Tuile label="Bonnes affaires affichées" valeur={vitrine ? nombre(vitrine.total) : null} sous={`au moins ${vitrine?.seuil_pct ?? 10} % sous la cote`} ton="argent" />
            <Tuile label="Annonces comparées à la cote" valeur={vitrine?.annonces_comparees != null ? nombre(vitrine.annonces_comparees) : null} />
          </div>
          <p className="zk-adm-lien"><Link to="/explore" className="zk-lien">Ouvrir la vitrine →</Link></p>

          {stats ? (
            <>
              <h2 className="zk-adm-titre">Utilisateurs</h2>
              <div className="zk-adm-tuiles">
                <Tuile label="Total inscrits" valeur={nombre(stats.users.total)} />
                <Tuile label="Comptes actifs" valeur={nombre(stats.users.active)} />
                <Tuile label="Administrateurs" valeur={nombre(stats.users.admins)} />
              </div>

              <h2 className="zk-adm-titre">Annonces</h2>
              <div className="zk-adm-tuiles">
                <Tuile label="Total en base" valeur={nombre(stats.listings.total)} />
                <Tuile label="Actives" valeur={nombre(stats.listings.active)} />
                <Tuile label="Arnaques détectées" valeur={nombre(stats.listings.fraud_detected)} ton="alerte" />
                <Tuile label="Alertes envoyées" valeur={nombre(stats.alerts.total_sent)} />
              </div>

              {stats.listings.sources && Object.keys(stats.listings.sources).length > 0 && (
                <>
                  <h2 className="zk-adm-titre">Sources</h2>
                  <ul className="zk-adm-sources">
                    {Object.entries(stats.listings.sources).map(([source, n]) => (
                      <li key={source}><span>{source}</span><b>{nombre(n)}</b></li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <p className="zk-adm-vide">Chargement des statistiques…</p>
          )}
        </div>
      )}

      {onglet === 'analytics' && (
        <div>
          {analytics ? (
            <>
              <h2 className="zk-adm-titre">Conversion (30 derniers jours)</h2>
              <div className="zk-adm-tuiles">
                <Tuile label="Visiteurs uniques" valeur={nombre(analytics.conversion.unique_visitors)} />
                <Tuile label="Inscrits" valeur={nombre(analytics.conversion.new_signups)} sous={`${analytics.conversion.visitor_to_signup_rate} % des visiteurs`} />
                <Tuile label="Abonnés" valeur={nombre(analytics.conversion.new_subscribers)} sous={`${analytics.conversion.signup_to_subscriber_rate} % des inscrits`} />
              </div>
              <p className="zk-adm-note">
                Taux de conversion global visiteur → abonné : <b>{analytics.conversion.visitor_to_subscriber_rate} %</b>
              </p>

              <h2 className="zk-adm-titre">Activité sur {analytics.period_days} jours</h2>
              <Graphique points={analytics.chart} />

              <h2 className="zk-adm-titre">Raisons de désinscription</h2>
              {analytics.cancellation_reasons.length === 0 ? (
                <p className="zk-adm-vide">Aucune raison de désinscription enregistrée pour le moment</p>
              ) : (
                <ul className="zk-adm-raisons">
                  {analytics.cancellation_reasons.map((c, i) => (
                    <li key={i} className="zk-carte">
                      <div>
                        <b>{c.email || `Utilisateur #${c.user_id}`}</b>
                        <small>{LIBELLES_PLANS[c.plan_type] || c.plan_type} · {jour(c.canceled_at)}</small>
                      </div>
                      <q>{c.reason}</q>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p className="zk-adm-vide">Chargement des analytics…</p>
          )}
        </div>
      )}

      {onglet === 'users' && (
        <div>
          <form className="zk-adm-recherche" onSubmit={(e) => { e.preventDefault(); chargerUtilisateurs(); }}>
            <label htmlFor="admin-recherche" className="zk-sr">Rechercher un utilisateur</label>
            <input
              id="admin-recherche" type="search" placeholder="Email ou nom d'utilisateur"
              value={recherche} onChange={(e) => setRecherche(e.target.value)}
            />
            <button type="submit" className="zk-bouton">Chercher</button>
          </form>

          {chargement ? (
            <p className="zk-adm-vide">Chargement…</p>
          ) : utilisateurs.length === 0 ? (
            <p className="zk-adm-vide">Aucun utilisateur trouvé</p>
          ) : (
            <div className="zk-adm-cadre">
              <table className="zk-adm-table">
                <thead>
                  <tr>
                    {['ID', 'Email', 'Nom', 'Inscription', 'Dernière connexion', 'Statut', 'Actions'].map((h) => <th key={h} scope="col">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {utilisateurs.map((u) => (
                    <tr key={u.id}>
                      <td data-label="ID"><span className="zk-adm-id">#{u.id}</span></td>
                      <td data-label="Email">
                        <span className="zk-adm-personne"><b>{u.email}</b>{u.is_admin && <small className="zk-adm-puce">Admin</small>}</span>
                      </td>
                      <td data-label="Nom">{u.username}</td>
                      <td data-label="Inscription">{jour(u.created_at)}</td>
                      <td data-label="Dernière connexion">
                        {u.last_login_at
                          ? new Date(u.last_login_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                          : 'Jamais'}
                      </td>
                      <td data-label="Statut"><span className={`zk-adm-etat${u.is_active ? '' : ' off'}`}>{u.is_active ? 'Actif' : 'Inactif'}</span></td>
                      <td className="actions">
                        <div className="zk-adm-actions">
                          <button type="button" className="zk-adm-btn" onClick={() => basculerActif(u.id)}>{u.is_active ? 'Désactiver' : 'Activer'}</button>
                          {!u.is_admin && <button type="button" className="zk-adm-btn bleu" onClick={() => basculerAdmin(u.id)}>+ Admin</button>}
                          {u.is_admin && u.email !== user?.email && <button type="button" className="zk-adm-btn rouge" onClick={() => basculerAdmin(u.id)}>− Admin</button>}
                          {!u.is_admin && <button type="button" className="zk-adm-btn rouge" onClick={() => supprimer(u.id, u.email)}>Supprimer</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {onglet === 'affiliate' && (
        <div>
          {chargement ? (
            <p className="zk-adm-vide">Chargement…</p>
          ) : !affiliation ? (
            <p className="zk-adm-vide">Erreur de chargement des données</p>
          ) : (
            <>
              <h2 className="zk-adm-titre">Affiliés ({affiliation.affiliates.length})</h2>
              {affiliation.affiliates.length === 0 ? <p className="zk-adm-vide">Aucun affilié</p> : (
                <div className="zk-adm-cadre">
                  <table className="zk-adm-table">
                    <thead><tr>{['Utilisateur', 'Code', 'Clics', 'Filleuls', 'En attente', 'Payé'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
                    <tbody>
                      {affiliation.affiliates.map((a) => (
                        <tr key={a.id}>
                          <td data-label="Utilisateur"><Personne nom={a.username} email={a.email} /></td>
                          <td data-label="Code"><span className="zk-adm-id">{a.code}</span></td>
                          <td data-label="Clics">{nombre(a.total_clicks)}</td>
                          <td data-label="Filleuls"><b>{nombre(a.total_referrals)}</b></td>
                          <td data-label="En attente">{euros(a.total_pending)}</td>
                          <td data-label="Payé"><b className="zk-adm-argent">{euros(a.total_paid)}</b></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h2 className="zk-adm-titre">Commissions en attente ({affiliation.pending_commissions.length})</h2>
              {affiliation.pending_commissions.length === 0 ? <p className="zk-adm-vide">Aucune commission en attente</p> : (
                <div className="zk-adm-cadre">
                  <table className="zk-adm-table">
                    <thead><tr>{['Affilié', 'Filleul', 'Montant', 'Offre', 'Date', 'Action'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
                    <tbody>
                      {affiliation.pending_commissions.map((c) => (
                        <tr key={c.id}>
                          <td data-label="Affilié"><Personne nom={c.affiliate_username} email={c.affiliate_email} /></td>
                          <td data-label="Filleul"><Personne nom={c.referred_username} email={c.referred_email} /></td>
                          <td data-label="Montant"><b>{euros(c.amount)}</b></td>
                          <td data-label="Offre"><Puce plan={c.plan_type} /></td>
                          <td data-label="Date">{jour(c.created_at)}</td>
                          <td className="actions">
                            <button type="button" className="zk-adm-btn vert" onClick={() => payer(c.id)}>Marquer payée</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h2 className="zk-adm-titre">Commissions payées ({affiliation.paid_commissions.length})</h2>
              {affiliation.paid_commissions.length === 0 ? <p className="zk-adm-vide">Aucune commission payée</p> : (
                <div className="zk-adm-cadre">
                  <table className="zk-adm-table">
                    <thead><tr>{['Affilié', 'Filleul', 'Montant', 'Offre', 'Date'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
                    <tbody>
                      {affiliation.paid_commissions.map((c) => (
                        <tr key={c.id}>
                          <td data-label="Affilié"><Personne nom={c.affiliate_username} email={c.affiliate_email} /></td>
                          <td data-label="Filleul"><Personne nom={c.referred_username} email={c.referred_email} /></td>
                          <td data-label="Montant"><b className="zk-adm-argent">{euros(c.amount)}</b></td>
                          <td data-label="Offre"><Puce plan={c.plan_type} /></td>
                          <td data-label="Date">{jour(c.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {onglet === 'vente' && <AdminVente signaler={signaler} onChange={chargerNouveautes} />}

      {onglet === 'garages' && <AdminGarages signaler={signaler} />}
      {onglet === 'recherche' && <AdminRecherche signaler={signaler} onChange={chargerNouveautes} />}

      {onglet === 'tools' && (
        <div className="zk-adm-outils">
          <h2 className="zk-adm-titre">Scraping</h2>
          <div className="zk-carte zk-adm-outil">
            <div>
              <h3>Lancer un cycle de scraping</h3>
              <p>Déclenche immédiatement le scraping AutoScout24 en arrière-plan.</p>
            </div>
            <button type="button" className="zk-bouton second" onClick={lancerScraping}>Lancer</button>
          </div>

          <h2 className="zk-adm-titre">Maintenance</h2>
          <div className="zk-carte zk-adm-outil">
            <div>
              <h3>Purger les vieilles annonces</h3>
              <p>Désactive les annonces non vues depuis plus de 30 jours.</p>
            </div>
            <button type="button" className="zk-bouton second zk-adm-danger" onClick={purger}>Purger</button>
          </div>

          <h2 className="zk-adm-titre">Informations</h2>
          <div className="zk-carte zk-adm-infos">
            <div><span>Email administrateur</span><b>{user?.email}</b></div>
            <div><span>Accès</span><b>Toutes les annonces, sans masquage</b></div>
          </div>
        </div>
      )}

    </Page>
  );
}
